import { useEnvContext } from "context/EnvContext";
import {
  ASSESSMENT_SLUG,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
} from "core/consts";
import { locationValue } from "core/model/utils/location/formatters";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import {
  Auction,
  MedicalProductType,
  MedicalSupplies,
  SearchType,
} from "core/types";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { PatientInfoSlugContext } from ".";
import {
  BooleanField,
  Categories,
  Category,
  PatientProfileInfoCard,
  StringField,
} from "./shared";
import { ComponentMappingItem, Filter, getCategories } from "./utils";

export const formatProductString = (
  product: MedicalProductType,
  translations: Translations,
) =>
  `${product?.name || ""} #${product.id} ${translations.general.group} ${
    product?.product_group || ""
  } ${product?.subgroup || ""}`;

const Products = ({
  translations,
  value,
}: {
  translations: Translations;
  value: Auction | null | undefined;
}) => {
  const getOntology = useGetOntology();
  const contents = [];

  if (
    value?.medical_supplies?.products &&
    value?.medical_supplies?.products?.length > 0
  ) {
    value.medical_supplies.products.forEach((product: MedicalProductType) => {
      if (!product?.id) return;
      const stringValue = formatProductString(product, translations);
      contents.push(
        <StringField
          key={product.id}
          testId="medical_supplies"
          value={stringValue}
        />,
      );
    });
  }
  if (value?.medical_supplies?.additional_information)
    contents.push(
      <StringField
        key="additional_information"
        prefix={translations.patient.additionalInformation}
        testId="medical_supplies_additional_information"
        value={value?.medical_supplies?.additional_information}
      />,
    );

  if (value?.specializations) {
    contents.push(
      <StringField
        key="home_care_specialization"
        prefix={translations.careproviderApp.notifications.specialization}
        testId="home_care_specialization"
        value={getOntology({
          key: value.specializations[0],
          type: "specializations",
        })}
      />,
    );
  }

  if (contents?.length === 0) return null;

  return contents?.length ? (
    <Category
      testId="medical_supplies"
      title={translations.assessments.medicalSupplies.medicalSupplies}
    >
      <VerticalLayout>{contents}</VerticalLayout>
    </Category>
  ) : null;
};

const DeliveryDetails = ({
  translations,
  value,
}: {
  translations: Translations;
  value: Auction | null | undefined;
}) => {
  const { deliveryAddressPrefix, deliveryAddressString } = locationValue(
    value,
    translations,
  );
  return (
    <Category
      testId="delivery_details"
      title={translations.assessments.medicalSupplies.deliveryDetails}
    >
      <StringField
        prefix={deliveryAddressPrefix}
        testId="delivery_details"
        value={deliveryAddressString}
      />
    </Category>
  );
};

const Prescription = ({
  translations,
  value,
}: {
  translations: Translations;
  value: MedicalSupplies | null | undefined;
}) => {
  return (
    <Category
      testId="prescription"
      title={translations.assessments.medicalSupplies.prescription}
    >
      {value?.prescription_issued_description ? (
        <StringField
          prefix={translations.assessments.medicalSupplies.prescriptionIssued}
          testId="prescription_issued_description"
          value={value?.prescription_issued_description}
        />
      ) : (
        <BooleanField
          label={translations.assessments.medicalSupplies.prescriptionIssued}
          value={value?.prescription_issued}
        />
      )}
    </Category>
  );
};

const medicalSuppliesComponentMapping: ComponentMappingItem[] = [
  {
    Component: Products,
    key: "products",
  },

  {
    Component: Prescription,
    key: "prescription",
  },
  {
    Component: DeliveryDetails,
    key: "delivery_details",
  },
];

export const medicalSuppliesFilters: Record<string, Filter> = {
  delivery_details: {
    exists: (auction) => auction != null,
    valueGetter: (auction) => auction,
  },
  prescription: {
    exists: (auction) => {
      return auction?.medical_supplies?.prescription_issued === true;
    },
    valueGetter: (auction) => auction?.medical_supplies,
  },
  products: {
    exists: (auction) =>
      auction?.medical_supplies?.products != null ||
      auction?.medical_supplies?.additional_information != null ||
      auction?.specializations != null,
    valueGetter: (auction) => auction,
  },
};

export function MedicalSuppliesSearchCard({ auction }: { auction: Auction }) {
  const getOntology = useGetOntology();
  const { isSenderApp } = useEnvContext();
  const translations = useTranslations();
  const categories = getCategories({
    auction,
    componentMapping: medicalSuppliesComponentMapping,
    filters: medicalSuppliesFilters,
    forClinic: isSenderApp,
  });
  if (
    !(
      [SEARCH_TYPE_MEDICAL_SUPPLIES, SEARCH_TYPE_HOME_CARE] as SearchType[]
    ).includes(auction.search_type)
  )
    return null;

  return (
    <PatientProfileInfoCard
      title={translations.assessments.medicalSupplies.medicalSupplies}
    >
      <PatientInfoSlugContext.Provider
        value={{
          assessmentSlug: ASSESSMENT_SLUG.MEDICAL_SUPPLIES_INFO,
          auctionId: auction.id,
          patientId: auction.patient.id,
        }}
      >
        <Categories
          auction={auction}
          categories={categories}
          getOntology={getOntology}
          translations={translations}
        />
      </PatientInfoSlugContext.Provider>
    </PatientProfileInfoCard>
  );
}
